
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { EmployeeStatistics, ProjectKPI } from "@/types/statistics";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { Clock, Users, Briefcase, Timer } from "lucide-react";

export const StatisticsDashboard = () => {
  const { data: employeeStats, isLoading: loadingEmployees } = useQuery({
    queryKey: ["employee-statistics"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("employee_statistics")
        .select("*");
      
      if (error) throw error;
      return data as EmployeeStatistics[];
    }
  });

  const { data: projectKPIs, isLoading: loadingProjects } = useQuery({
    queryKey: ["project-kpis"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("project_kpis")
        .select("*");
      
      if (error) throw error;
      return data as ProjectKPI[];
    }
  });

  if (loadingEmployees || loadingProjects) {
    return <div>Indlæser statistikker...</div>;
  }

  return (
    <Tabs defaultValue="employees" className="space-y-4">
      <TabsList>
        <TabsTrigger value="employees">Medarbejder Statistik</TabsTrigger>
        <TabsTrigger value="projects">Projekt KPIer</TabsTrigger>
      </TabsList>

      <TabsContent value="employees" className="space-y-4">
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Medarbejdere</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{employeeStats?.length || 0}</div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Samlet Arbejdstid</CardTitle>
              <Clock className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {employeeStats?.reduce((acc, curr) => acc + curr.total_hours, 0).toFixed(1)} t
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Overtid</CardTitle>
              <Timer className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {employeeStats?.reduce((acc, curr) => acc + curr.overtime_hours, 0).toFixed(1)} t
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Aktive Projekter</CardTitle>
              <Briefcase className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{projectKPIs?.length || 0}</div>
            </CardContent>
          </Card>
        </div>

        <Card className="pt-4">
          <CardHeader>
            <CardTitle>Arbejdstimer pr. Medarbejder</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={employeeStats}>
                <XAxis 
                  dataKey="first_name" 
                  label={{ value: 'Medarbejder', position: 'bottom' }}
                />
                <YAxis label={{ value: 'Timer', angle: -90, position: 'left' }} />
                <Tooltip />
                <Bar dataKey="total_hours" fill="#2563eb" name="Normal tid" />
                <Bar dataKey="overtime_hours" fill="#dc2626" name="Overtid" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </TabsContent>

      <TabsContent value="projects" className="space-y-4">
        <div className="grid gap-4 md:grid-cols-2">
          {projectKPIs?.map((project) => (
            <Card key={project.project_id}>
              <CardHeader>
                <CardTitle>{project.project_title}</CardTitle>
              </CardHeader>
              <CardContent>
                <dl className="space-y-2">
                  <div className="flex justify-between">
                    <dt className="font-medium">Antal opgaver:</dt>
                    <dd>{project.total_tasks}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="font-medium">Antal medarbejdere:</dt>
                    <dd>{project.total_employees}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="font-medium">Total timeforbrug:</dt>
                    <dd>{project.total_hours} timer</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="font-medium">Gns. estimeret tid pr. opgave:</dt>
                    <dd>{project.avg_estimated_hours_per_task} timer</dd>
                  </div>
                  <div className="flex justify-between text-red-600">
                    <dt className="font-medium">Overtid:</dt>
                    <dd>{project.total_overtime_hours} timer</dd>
                  </div>
                </dl>
              </CardContent>
            </Card>
          ))}
        </div>
      </TabsContent>
    </Tabs>
  );
};
